import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Minus, Plus, ArrowRight } from "lucide-react";
import { SEO } from "../components/SEO";
import { Reveal } from "../components/Reveal";
import { useContactData } from "../hooks/useContactData";

const CATEGORY_LABELS = {
  general: "General",
  features: "Features",
  security: "Security",
  pricing: "Pricing",
  onboarding: "Onboarding",
};

export const FAQ = () => {
  const contactData = useContactData();
  const [activeCategory, setActiveCategory] = useState("all");
  const [openFaq, setOpenFaq] = useState(0);

  const categories = useMemo(() => {
    const set = new Set(contactData.faqs.map((f) => f.category));
    return ["all", ...Array.from(set)];
  }, [contactData.faqs]);

  const filtered = useMemo(() => {
    if (activeCategory === "all") return contactData.faqs;
    return contactData.faqs.filter((f) => f.category === activeCategory);
  }, [activeCategory, contactData.faqs]);

  const faqSchema = useMemo(
    () => ({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: contactData.faqs.map((faq) => ({
        "@type": "Question",
        name: faq.question,
        acceptedAnswer: {
          "@type": "Answer",
          text: faq.answer,
        },
      })),
    }),
    [contactData.faqs],
  );

  return (
    <>
      <SEO
        title="Frequently Asked Questions"
        description="Answers about OOMS onboarding, pricing, multi-branch setup, WhatsApp broadcasts, RBAC security, and data export."
        canonicalUrl={`${contactData.websiteUrl}/faq`}
        schema={faqSchema}
        breadcrumbs={[
          { name: "Home", item: "/" },
          { name: "FAQ", item: "/faq" },
        ]}
      />

      <section className="pt-20 pb-12 text-center px-4 max-w-3xl mx-auto space-y-5">
        <span className="text-xs font-black uppercase tracking-[0.2em] text-primary-600 dark:text-primary-400 font-heading">
          Support Center
        </span>
        <h1 className="font-heading font-black text-4xl sm:text-5xl text-foreground leading-[0.95] tracking-tighter">
          Frequently Asked Questions
        </h1>
        <p className="text-text-sub text-lg font-medium">
          Everything partners ask before switching from Excel and chat-based
          coordination to OOMS.
        </p>
      </section>

      <section className="pb-8 px-4 flex flex-wrap justify-center gap-3 max-w-4xl mx-auto">
        {categories.map((cat) => (
          <button
            key={cat}
            type="button"
            onClick={() => {
              setActiveCategory(cat);
              setOpenFaq(0);
            }}
            className={`rounded-2xl px-5 py-2.5 font-heading text-[11px] font-black uppercase tracking-wider transition-all ${
              activeCategory === cat
                ? "bg-primary-600 text-white shadow-lg shadow-primary-600/25"
                : "bg-surface border border-border text-text-sub hover:border-primary-400"
            }`}
          >
            {cat === "all" ? "All" : CATEGORY_LABELS[cat] || cat}
          </button>
        ))}
      </section>

      <section className="pb-20 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto">
        <div className="space-y-4">
          {filtered.map((faq, idx) => {
            const isOpen = openFaq === idx;
            return (
              <Reveal key={faq.question} delay={Math.min(idx * 0.04, 0.2)}>
                <div className="rounded-[24px] border border-border bg-surface overflow-hidden">
                  <button
                    type="button"
                    onClick={() => setOpenFaq(isOpen ? -1 : idx)}
                    className="w-full flex items-center justify-between gap-4 p-6 text-left font-heading font-black text-base text-foreground hover:bg-surface-hover"
                  >
                    <span>{faq.question}</span>
                    <div
                      className={`shrink-0 p-2 rounded-xl ${
                        isOpen
                          ? "bg-primary-600 text-white"
                          : "bg-muted text-primary-600"
                      }`}
                    >
                      {isOpen ? (
                        <Minus className="h-4 w-4" />
                      ) : (
                        <Plus className="h-4 w-4" />
                      )}
                    </div>
                  </button>
                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.25 }}
                      >
                        <div className="px-6 pb-6 pt-1 text-text-sub text-base leading-relaxed font-medium border-t border-border">
                          {faq.answer}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </Reveal>
            );
          })}
        </div>

        <Reveal className="mt-14 text-center space-y-4">
          <p className="text-text-sub font-medium">
            Still deciding? Talk to a product specialist.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 rounded-2xl bg-primary-600 px-8 py-4 font-heading font-black text-white hover:bg-primary-700"
          >
            Contact Sales <ArrowRight className="h-4 w-4" />
          </Link>
        </Reveal>
      </section>
    </>
  );
};

export default FAQ;
